// src/pages/ProfitPage.js
// รายงานกำไรสุทธิ (รายได้ - รายจ่าย) แบบรายเดือน ย้อนหลัง 1/3/6/12 เดือน
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { Bar } from 'react-chartjs-2';
import { useNotification } from '../NotificationContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const ProfitPage = () => {
  const [mode, setMode] = useState('recent'); // 'recent', 'year', 'month'
  const [months, setMonths] = useState(3);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();

  const load = async () => {
    try {
      setLoading(true);
      let params = {};
      if (mode === 'recent') {
        params = { months };
      } else if (mode === 'year') {
        params = { year: selectedYear };
      } else if (mode === 'month') {
        params = { year: selectedYear, month: selectedMonth };
      }

      const r = await api.get('/reports/net-profit', { params });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      console.error('load net profit failed', err);
      setRows([]);
      addNotification?.(err.response?.data?.message || 'โหลดรายงานกำไรสุทธิไม่สำเร็จ', 'danger');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [mode, months, selectedYear, selectedMonth]);

  const fmt = (v) => (Number(v) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const chartData = useMemo(() => {
    let labels = [];
    if (mode === 'month') {
      // Daily view: show Day
      labels = rows.map(r => `${r.day}/${r.month}`);
    } else {
      // Monthly view: show Month/Year
      labels = rows.map(r => `${String(r.month).padStart(2, '0')}/${String(r.year).slice(-2)}`);
    }

    return {
      labels,
      datasets: [
        { label: 'รายได้ (฿)', data: rows.map(r => r.revenue), backgroundColor: '#59a14f' },
        { label: 'รายจ่าย (฿)', data: rows.map(r => r.expenses), backgroundColor: '#e15759' },
        { label: 'กำไรสุทธิ (฿)', data: rows.map(r => r.net_profit), backgroundColor: '#4e79a7' },
      ]
    };
  }, [rows, mode]);

  const chartOptions = { responsive: true, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)} ฿` } } }, scales: { y: { ticks: { callback: (v) => v.toLocaleString('th-TH') } } } };

  const total = useMemo(() => rows.reduce((acc, r) => ({
    revenue: acc.revenue + (Number(r.revenue) || 0),
    expenses: acc.expenses + (Number(r.expenses) || 0),
    net: acc.net + (Number(r.net_profit) || 0),
  }), { revenue: 0, expenses: 0, net: 0 }), [rows]);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const ys = [];
    for (let y = currentYear; y >= 2020; y--) ys.push(y);
    return ys;
  }, []);

  const monthNames = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];

  return (
    <div className="dashboard-page container py-3">
      {/* Hero Header */}
      <div className="dashboard-hero mb-3">
        <div>
          <div className="dashboard-hero-title">
            <i className="bi bi-graph-up-arrow me-2"></i>รายงานกำไรสุทธิ
          </div>
          <div className="dashboard-hero-subtitle">สรุปรายได้ หัก รายจ่าย (รายเดือน / รายวัน)</div>
        </div>
      </div>

      <div className="d-flex flex-wrap justify-content-end align-items-center mb-3 gap-2">
        {/* Mode Selector */}
        <div className="btn-group btn-group-sm" role="group">
          <button type="button" className={`btn ${mode === 'recent' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setMode('recent')}>ล่าสุด</button>
          <button type="button" className={`btn ${mode === 'year' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setMode('year')}>รายปี</button>
          <button type="button" className={`btn ${mode === 'month' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setMode('month')}>รายเดือน</button>
        </div>

        {/* Controls based on mode */}
        {mode === 'recent' && (
          <div className="card bg-light border-0 px-3 py-2">
            <div className="d-flex align-items-center">
              <div className="me-2 text-muted small fw-bold">ย้อนหลัง:</div>
              <div className="btn-group btn-group-sm" role="group">
                {[1, 3, 6, 12].map(m => (
                  <button key={m} type="button" className={`btn ${months === m ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setMonths(m)}>{m} เดือน</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {(mode === 'year' || mode === 'month') && (
          <div className="card bg-light border-0 px-3 py-2">
            <div className="d-flex align-items-center gap-2">
              <select className="form-select form-select-sm" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {mode === 'month' && (
                <select className="form-select form-select-sm" value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                  {monthNames.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="row g-3">
        <div className="col-lg-12">
          <div className="card card-ks-soft mb-3">
            <div className="card-body">
              <h5 className="card-title mb-3 fw-bold text-secondary"><i className="bi bi-bar-chart-line me-2"></i>กราฟสรุป</h5>
              {loading ? <div className="text-center py-5"><span className="spinner-border text-primary"></span></div> : (
                rows.length === 0 ? <div className="text-center text-muted py-5">ยังไม่มีข้อมูล</div> : (
                  <div style={{ maxHeight: 380 }}>
                    <Bar data={chartData} options={chartOptions} />
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="card card-ks-soft">
            <div className="card-body">
              <h5 className="card-title mb-3 fw-bold text-secondary"><i className="bi bi-table me-2"></i>ตารางสรุป</h5>
              {rows.length === 0 ? <div className="text-center text-muted py-4">ยังไม่มีข้อมูล</div> : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light text-muted">
                      <tr>
                        <th>{mode === 'month' ? 'วันที่' : 'เดือน/ปี'}</th>
                        <th className="text-end">รายได้</th>
                        <th className="text-end">รายจ่าย</th>
                        <th className="text-end">กำไรสุทธิ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={idx}>
                          <td>
                            <span className="badge bg-light text-dark border">
                              {mode === 'month'
                                ? `${String(r.day).padStart(2, '0')}/${String(r.month).padStart(2, '0')}/${r.year}`
                                : `${String(r.month).padStart(2, '0')}/${r.year}`
                              }
                            </span>
                          </td>
                          <td className="text-end text-success">{fmt(r.revenue)} ฿</td>
                          <td className="text-end text-danger">{fmt(r.expenses)} ฿</td>
                          <td className={`text-end fw-bold ${Number(r.net_profit) >= 0 ? '' : 'text-danger'}`} style={Number(r.net_profit) >= 0 ? { color: "#d4af37" } : {}}>{fmt(r.net_profit)} ฿</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="fw-bold bg-light">
                        <td>รวมทั้งหมด</td>
                        <td className="text-end text-success">{fmt(total.revenue)} ฿</td>
                        <td className="text-end text-danger">{fmt(total.expenses)} ฿</td>
                        <td className="text-end" style={{ color: "#d4af37" }}>{fmt(total.net)} ฿</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitPage;
